import { FoundationErrorEvent, PlayerEvent } from '@foundation-player/core';
import { PlayerError } from '@foundation-player/error-codes';
import { PACKAGE_NAME_TO_BUNDLE_NAME, PackageName } from './package-name';

/**
 * Resolve to a global name on the window and return the requested bundle if it exists.
 *
 * @internal
 */
export function resolveByBundleName<ReturnType>(
  globalName: (typeof PACKAGE_NAME_TO_BUNDLE_NAME)[keyof typeof PACKAGE_NAME_TO_BUNDLE_NAME],
): ReturnType | undefined {
  const parts = globalName.split('.');
  let currentPart = 0;
  let value: unknown = window;

  // Our code is mounted in the window at for example window.foundation.player.bitmovin
  // Every package defines a global name like 'foundation.player.bitmovin'
  // we now have to check if the script is loaded correctly and return the bundle
  // if any key is missing, we return with undefined
  // otherwise we resolve until the last part of the key is reached and the bundle is thus correctly loaded
  // this while a poor man's json path function
  while (parts[currentPart]) {
    // stop if a part does not exist, full path must match
    if (!(typeof value === 'object' && value && parts[currentPart] in value)) {
      return undefined;
    }
    value = (value as Record<string, unknown>)[parts[currentPart]];
    currentPart++;
  }

  return value as ReturnType;
}

/**
 * Resolve to a package on the window and return the requested bundle if it exists.
 *
 * @internal
 */
export function resolveByPackageName<ReturnType>(packageName: PackageName): ReturnType | undefined {
  return resolveByBundleName<ReturnType>(PACKAGE_NAME_TO_BUNDLE_NAME[packageName]);
}

/**
 * Resolve to a package on the window and return the requested bundle if it exists.
 *
 * @internal
 */
export function resolveByPackageNameOrThrow<ReturnType>(packageName: PackageName): ReturnType {
  const bundle = resolveByPackageName<ReturnType>(packageName);

  if (!bundle) {
    throw new FoundationErrorEvent(PlayerEvent.Error, {
      message: `Could not resolve package "${packageName}"`,
      code: PlayerError.PLAYER_DEPENDENCY_MISSING_52120.code,
    });
  }

  return bundle;
}
